import React, { useState, useRef, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, Upload, Trash2, CheckCircle, Circle, Camera } from 'lucide-react';
import './EditPermitModal.css';

const EditPermitModal = ({ permit, onClose, onSuccess, isAdmin = false }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    permit_number: permit?.permit_number || '',
    issue_date: permit?.issue_date ? new Date(permit.issue_date).toISOString().split('T')[0] : '',
    expiration_date: permit?.expiration_date ? new Date(permit.expiration_date).toISOString().split('T')[0] : '',
    employee_name: permit?.employee_name || '',
    custom_permit_name: permit?.custom_permit_name || '',
  });
  const [permitImage, setPermitImage] = useState(permit?.permit_image || null);
  const [imagePreview, setImagePreview] = useState(permit?.permit_image || null);
  const fileInputRef = useRef(null);
  
  // Firearms qualification tracking
  const [qualifications, setQualifications] = useState({
    qual_1: permit?.qual_1 || { date: '', completed: false, photo: null },
    qual_2: permit?.qual_2 || { date: '', completed: false, photo: null },
    qual_3: permit?.qual_3 || { date: '', completed: false, photo: null },
    qual_4_renewal: permit?.qual_4_renewal || { date: '', completed: false, photo: null },
  });
  const qualPhotoRefs = {
    qual_1: useRef(null),
    qual_2: useRef(null),
    qual_3: useRef(null),
    qual_4_renewal: useRef(null),
  };
  
  // Guard card training tracking
  const [training32hrsCompleted, setTraining32hrsCompleted] = useState(permit?.training_32hrs_completed);

  // Initialize qualifications from permit data
  useEffect(() => {
    if (permit) {
      setQualifications({
        qual_1: {
          date: permit.qual_1?.date ? new Date(permit.qual_1.date).toISOString().split('T')[0] : '',
          completed: permit.qual_1?.completed || false,
          photo: permit.qual_1?.photo || null
        },
        qual_2: {
          date: permit.qual_2?.date ? new Date(permit.qual_2.date).toISOString().split('T')[0] : '',
          completed: permit.qual_2?.completed || false,
          photo: permit.qual_2?.photo || null
        },
        qual_3: {
          date: permit.qual_3?.date ? new Date(permit.qual_3.date).toISOString().split('T')[0] : '',
          completed: permit.qual_3?.completed || false,
          photo: permit.qual_3?.photo || null
        },
        qual_4_renewal: {
          date: permit.qual_4_renewal?.date ? new Date(permit.qual_4_renewal.date).toISOString().split('T')[0] : '',
          completed: permit.qual_4_renewal?.completed || false,
          photo: permit.qual_4_renewal?.photo || null
        },
      });
      setTraining32hrsCompleted(permit.training_32hrs_completed);
    }
  }, [permit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPermitImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPermitImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle qualification date/completed changes
  const handleQualChange = (qualKey, field, value) => {
    setQualifications(prev => ({
      ...prev,
      [qualKey]: {
        ...prev[qualKey],
        [field]: value
      }
    }));
  };

  // Handle qualification photo upload
  const handleQualPhotoUpload = (qualKey, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        handleQualChange(qualKey, 'photo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveQualPhoto = (qualKey) => {
    handleQualChange(qualKey, 'photo', null);
    if (qualPhotoRefs[qualKey].current) {
      qualPhotoRefs[qualKey].current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Helper function to safely parse date string to ISO format
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        // If already in ISO format with time, return as is
        if (dateStr.includes('T')) return dateStr;
        // For YYYY-MM-DD format, append time to avoid timezone issues
        const [year, month, day] = dateStr.split('-');
        // Ensure year is 4 digits
        const fullYear = year.length === 4 ? year : year.padStart(4, '20');
        return `${fullYear}-${month}-${day}T12:00:00.000Z`;
      };

      const updateData = {
        permit_number: formData.permit_number,
        issue_date: parseDate(formData.issue_date),
        expiration_date: parseDate(formData.expiration_date),
        permit_image: permitImage,
        employee_name: formData.employee_name || null,
        custom_permit_name: formData.custom_permit_name || null,
      };

      // Add firearms qualification data
      if (permit?.permit_type === 'firearms') {
        updateData.qual_1 = {
          date: qualifications.qual_1.date ? parseDate(qualifications.qual_1.date) : null,
          completed: qualifications.qual_1.completed,
          photo: qualifications.qual_1.photo
        };
        updateData.qual_2 = {
          date: qualifications.qual_2.date ? parseDate(qualifications.qual_2.date) : null,
          completed: qualifications.qual_2.completed,
          photo: qualifications.qual_2.photo
        };
        updateData.qual_3 = {
          date: qualifications.qual_3.date ? parseDate(qualifications.qual_3.date) : null,
          completed: qualifications.qual_3.completed,
          photo: qualifications.qual_3.photo
        };
        updateData.qual_4_renewal = {
          date: qualifications.qual_4_renewal.date ? parseDate(qualifications.qual_4_renewal.date) : null,
          completed: qualifications.qual_4_renewal.completed,
          photo: qualifications.qual_4_renewal.photo
        };
      }

      // Add guard card specific fields
      if (permit?.permit_type === 'guard_card') {
        updateData.training_32hrs_completed = training32hrsCompleted;
      }

      // Use regular endpoint - backend handles admin permissions internally
      const endpoint = `/permits/${permit.id}`;

      const response = await axiosInstance.put(endpoint, updateData);
      toast.success('Permit updated successfully');
      onSuccess();
    } catch (error) {
      // Handle 202 Accepted - change request submitted
      if (error.response?.status === 202) {
        toast.success(error.response.data.detail || 'Your changes have been submitted for approval');
        onSuccess();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update permit');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get display name for permit type
  const getPermitTypeName = (type) => {
    const typeMap = {
      'firearms': 'BSIS Exposed Firearm Permit',
      'guard_card': 'BSIS Guard Card',
      'baton': 'BSIS Baton Permit',
      'bsis_ppo': 'BSIS PPO License',
      'bsis_qm': 'BSIS QM License',
      'bsis_pi': 'BSIS Private Investigator License',
      'bsis_locksmith': 'BSIS Locksmith License',
      'bsis_alarm': 'BSIS Alarm Company License',
      'ccw_ca': 'California CCW Permit',
      'ccw_out_of_state': 'Out of State CCW Permit',
      'hr218': 'HR218 LEOSA Permit',
      'doj_coe': 'DOJ COE License',
      'doj_firearms_instructor': 'CA DOJ Firearms Instructor License',
      'doj_ccw_instructor': 'CA DOJ CCW Instructor License',
      'first_aid': 'First Aid License',
      'cpr': 'CPR License',
      'tecc': 'TECC License',
      'axon_taser_instructor': 'AXON TASER Instructor License',
      'sabre_oc_instructor': 'SABRE OC Instructor License',
      'nra_instructor': 'NRA Instructor License',
      'uscca_instructor': 'USCCA Instructor License',
      'crpa_instructor': 'CRPA Instructor License',
      'drivers_license': 'Drivers License',
      'passport': 'Passport',
      'vehicle_insurance': 'Vehicle Insurance',
      'other': 'Custom Permit',
    };
    return typeMap[type] || type;
  };

  // Render qualification field
  const renderQualificationField = (qualKey, label) => (
    <div key={qualKey} className="qualification-field">
      <div className="qual-header">
        <span className="qual-label">{label}</span>
        <button
          type="button"
          className={`qual-complete-btn ${qualifications[qualKey].completed ? 'completed' : ''}`}
          onClick={() => handleQualChange(qualKey, 'completed', !qualifications[qualKey].completed)}
        >
          {qualifications[qualKey].completed ? (
            <><CheckCircle size={18} /> Completed</>
          ) : (
            <><Circle size={18} /> Mark Complete</>
          )}
        </button>
      </div>
      <div className="qual-inputs">
        <div className="qual-date-input">
          <label>Date Completed</label>
          <input
            type="date"
            className="input-field"
            value={qualifications[qualKey].date}
            onChange={(e) => handleQualChange(qualKey, 'date', e.target.value)}
          />
        </div>
        <div className="qual-photo-input">
          <label>Qualification Form Photo</label>
          {qualifications[qualKey].photo ? (
            <div className="qual-photo-preview">
              <img src={qualifications[qualKey].photo} alt={`${label} form`} />
              <button
                type="button"
                className="remove-qual-photo-btn"
                onClick={() => handleRemoveQualPhoto(qualKey)}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ) : (
            <div
              className="qual-photo-upload-box"
              onClick={() => qualPhotoRefs[qualKey].current?.click()}
            >
              <Camera size={18} />
              <span>Add Photo</span>
            </div>
          )}
          <input
            ref={qualPhotoRefs[qualKey]}
            type="file"
            accept="image/*"
            onChange={(e) => handleQualPhotoUpload(qualKey, e)}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" data-testid="edit-permit-modal">
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className={`modal-content ${permit?.permit_type === 'firearms' ? 'modal-content-large' : ''}`}>
        <div className="modal-header">
          <div>
            <h2 data-testid="modal-title">Edit Permit</h2>
            <span className="permit-type-badge">{permit?.custom_permit_name || getPermitTypeName(permit?.permit_type)}</span>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="permit-form" data-testid="permit-form">
          {/* Custom Permit Name Field - Only shown for "other" type */}
          {permit?.permit_type === 'other' && (
            <div className="form-group custom-permit-section">
              <label className="label">Permit Type Name</label>
              <input
                type="text"
                name="custom_permit_name"
                className="input-field"
                value={formData.custom_permit_name}
                onChange={handleInputChange}
                placeholder="Enter the name of your permit"
                data-testid="custom-permit-name-input"
              />
            </div>
          )}

          {/* Employee Name Field */}
          {permit?.employee_name !== undefined && (
            <div className="form-group employee-section">
              <label className="label">Employee Name</label>
              <input
                type="text"
                name="employee_name"
                className="input-field"
                value={formData.employee_name}
                onChange={handleInputChange}
                placeholder="Enter employee's full name"
                data-testid="employee-name-input"
              />
            </div>
          )}

          <div className="form-group">
            <label className="label">Permit Number</label>
            <input
              type="text"
              name="permit_number"
              className="input-field"
              value={formData.permit_number}
              onChange={handleInputChange}
              placeholder="e.g., EXP-12345"
              required
              data-testid="permit-number-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="label">Issue Date</label>
              <input
                type="date"
                name="issue_date"
                className="input-field"
                value={formData.issue_date}
                onChange={handleInputChange}
                required
                data-testid="issue-date-input"
              />
            </div>

            <div className="form-group">
              <label className="label">Expiration Date</label>
              <input
                type="date"
                name="expiration_date"
                className="input-field"
                value={formData.expiration_date}
                onChange={handleInputChange}
                required
                data-testid="expiration-date-input"
              />
            </div>
          </div>

          {/* GUARD CARD - 32 Hours Training Section */}
          {permit?.permit_type === 'guard_card' && (
            <div className="form-group guard-card-training-section">
              <label className="label">Have You Completed Your 32 HRS Additional Training?</label>
              <div className="training-checkbox-group">
                <label className={`training-checkbox ${training32hrsCompleted === true ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="training_32hrs"
                    checked={training32hrsCompleted === true}
                    onChange={() => setTraining32hrsCompleted(true)}
                  />
                  <CheckCircle size={20} />
                  <span>YES</span>
                </label>
                <label className={`training-checkbox ${training32hrsCompleted === false ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="training_32hrs"
                    checked={training32hrsCompleted === false}
                    onChange={() => setTraining32hrsCompleted(false)}
                  />
                  <Circle size={20} />
                  <span>NO</span>
                </label>
              </div>
            </div>
          )}

          {/* FIREARMS - Qualification Tracking Section */}
          {permit?.permit_type === 'firearms' && (
            <div className="form-group firearms-qualification-section">
              <label className="label section-label">Qualification Tracking</label>
              <p className="field-hint">Track your 4 required qualifications (4-6 month intervals)</p>
              <div className="qualifications-grid">
                {renderQualificationField('qual_1', 'BSIS EXPOSED FIREARM QUALIFICATION 1')}
                {renderQualificationField('qual_2', 'BSIS EXPOSED FIREARM QUALIFICATION 2')}
                {renderQualificationField('qual_3', 'BSIS EXPOSED FIREARM QUALIFICATION 3')}
                {renderQualificationField('qual_4_renewal', 'BSIS EXPOSED FIREARM QUALIFICATION 4 (Renewal)')}
              </div>
            </div>
          )}

          {/* Permit Photo Upload */}
          <div className="form-group">
            <label className="label">Permit Photo</label>
            <div className="photo-upload-section">
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Permit preview" className="image-preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveImage}
                    data-testid="remove-image-btn"
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  className="photo-upload-box"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-upload-box"
                >
                  <Upload size={24} />
                  <span>Click to upload permit photo</span>
                  <span className="upload-hint">JPG, PNG up to 5MB</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                data-testid="photo-input"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-outline"
              onClick={onClose}
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPermitModal;
